// Portfolio sidebar (FOUNDER-OS; 12): Focus, Brief and Thinking, then the COMPANIES caption with
// one row per company of the portfolio (its health dot and how many things need the founder
// there), each opening its drill-down. The list is Focus's own query. FOUNDER-OS phase 1 (design
// §3.2): Chart, Goals and Issues moved to KPIs (Chart, Priorities, Issues).
import { CountPill, NavList, NavRow, SidebarCaption, SidebarPanel } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { LightbulbIcon, type LucideIcon, NewspaperIcon, TelescopeIcon } from "lucide-react";
import type { SidebarProps } from "../../extensions/registry";
import { focusQuery } from "../../features/portfolio/portfolio-queries";
import { HealthDot, SwitcherTile } from "../company-switcher";

const ROWS: { to: string; label: string; icon: LucideIcon; exact?: boolean }[] = [
  { to: "/c/$slug/portfolio", label: "Focus", icon: TelescopeIcon, exact: true },
  { to: "/c/$slug/portfolio/brief", label: "Brief", icon: NewspaperIcon },
  { to: "/c/$slug/portfolio/thinking", label: "Thinking", icon: LightbulbIcon },
];

export function PortfolioSidebar({ company, children }: SidebarProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const focus = useQuery(focusQuery(api, company.id));
  const params = { slug: company.slug };
  const rows = [...(focus.data?.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <SidebarPanel title="Portfolio">
      <NavList>
        {ROWS.map((r) => (
          <NavRow
            key={r.to}
            icon={r.icon}
            label={r.label}
            render={
              <Link
                to={r.to as never}
                params={params as never}
                activeOptions={{ exact: !!r.exact, includeSearch: false }}
              />
            }
          />
        ))}
      </NavList>
      <SidebarCaption label="Companies" />
      {focus.isPending ? null : rows.length === 0 ? (
        <p className="px-4 text-xs text-muted-foreground">No company yet</p>
      ) : (
        <NavList aria-label="Portfolio companies">
          {rows.map((r) => (
            <NavRow
              key={r.company_id}
              lead={<SwitcherTile name={r.name} portfolio={false} />}
              label={r.name}
              trailing={
                <span className="flex items-center gap-1.5">
                  {r.needs_you.length ? (
                    <CountPill count={r.needs_you.length} tone="warning" />
                  ) : null}
                  <HealthDot band={r.band} />
                </span>
              }
              render={
                <Link
                  to="/c/$slug/portfolio/companies/$companyId"
                  params={{ slug: company.slug, companyId: r.company_id }}
                  activeOptions={{ includeSearch: false }}
                />
              }
            />
          ))}
        </NavList>
      )}
      {children}
    </SidebarPanel>
  );
}
